import React from 'react';

import type { AnalyticsHost } from './api';
import type { LinkPick } from './filters';
import {
  attributionActive,
  creditedOnly,
  withoutFacet,
  countriesQuery,
  num,
  shapeCountries,
  shapeSources,
  sourcesQuery,
  visitorsQuery,
  type Attribution,
  type CountryRow,
  type LinkRow,
  type Match,
  type PageScope,
  type SourceReach,
  type SourceRow,
  type Span,
} from './queries';
import { affiliateOf, canonicalSelection, emptyLinks, liftsExclusion, selectionEmpty } from './selection';
import {
  makeAsker,
  expandLinkExclusions,
  readLinks,
  readSourceReach,
  scopeOf,
  type Section,
} from './useWebsiteAnalytics';

export type CountStatus = 'idle' | 'loading' | 'ready' | 'error';

export type ChoiceCount = { status: Exclude<CountStatus, 'idle'> | 'unavailable'; visitors: number };

export type FilterCounts = {
  /** Visitors per country (the country picker), `idle` until asked for. */
  countries: { status: CountStatus; rows: CountryRow[] };
  /** Visitors per affiliate and per campaign (by the `aff_id` part their links carry), each once asked for. */
  affiliates: ReadonlyMap<string, ChoiceCount>;
  campaigns: ReadonlyMap<string, ChoiceCount>;
  /** The source picker's counts (as the cards' `sources` and `sourceReach`), loading until asked for. */
  sources: Section<SourceRow[]>;
  sourceReach: Section<SourceReach>;
  /** The link picker's counts (as the cards' `links`), loading until asked for. */
  links: Section<LinkRow[]>;
  /** The links are counted credited (picking one keeps a filter no arrival can name) with pages picked: a credited
   *  count cannot be split by page, so each link's count is unavailable. */
  linksUnavailable: boolean;
};

/** The filters' counts are asked two at a time, like the cards. */
const CONCURRENCY = 2;

export type FilterCountsInput = {
  tenantId: number | undefined;
  /** The brand's addresses are known (the cards' first read is in). */
  ready: boolean;
  hosts: AnalyticsHost[];
  pages: readonly PageScope[];
  /** Every page but `pages`. */
  pagesOut?: boolean;
  span: Span;
  /** The filters as they stand in the sheet (the counts say what picking a choice shows once they are applied). */
  attribution: Attribution;
  /** The countries ticked (every count but the countries' own keeps them). */
  countries: Match | null;
  attributionAvailable: boolean;
  conditionsAvailable: boolean;
  /** The links the link picker lists besides those this hook's links read finds (the brand's, those the picks name, the
   *  cards' while they stand in): with them, which left-out affiliate a click takes back (see `liftsExclusion`). */
  listedLinks: readonly string[];
  /** What to count: the countries, these affiliates and campaigns, the sources, the links (the filter on screen). */
  want: {
    countries: boolean;
    affiliates: readonly string[];
    campaigns: readonly string[];
    sources: boolean;
    links: boolean;
  };
};

/** A read's newest answer, with the filters it was read under (`sig`), and its ready answers by the filters each was
 *  read under (`readyBySig`, the newest one included once it is ready): going back to those filters, or a re-read there
 *  that fails, shows them. At most `KEPT_KEYS` ready answers are kept in all, the newest one included. */
type Kept<V> = { sig: string; value: V; readyBySig: ReadonlyMap<string, V> };
type Entry<T> = Kept<Section<T>>;
type ChoiceKind = 'affiliates' | 'campaigns';
const CHOICE_KINDS: readonly ChoiceKind[] = ['affiliates', 'campaigns'];
type Held = {
  countries?: Entry<CountryRow[]>;
  sources?: Entry<SourceRow[]>;
  sourceReach?: Entry<SourceReach>;
  links?: Entry<LinkRow[]>;
  affiliates?: ReadonlyMap<string, Kept<ChoiceCount>>;
  campaigns?: ReadonlyMap<string, Kept<ChoiceCount>>;
};
/** Reads waiting for a place, and how many hold one. */
type Queued = { signal: AbortSignal; read: () => Promise<void> };
type Queue = { pending: Queued[]; running: number };

const LOADING = { status: 'loading', data: null } as const;
const FAILED = { status: 'error', data: null } as const;
const UNAVAILABLE: ChoiceCount = { status: 'unavailable', visitors: 0 };
const NO_VALUES: readonly string[] = [];
const NOTHING_KEPT: ReadonlyMap<string, never> = new Map<string, never>();
/** At most this many ready answers are kept per read, the newest one included: going back and forth between a few
 *  filters in the sheet stays instant, while each read holds at most 8 counts or 8 copies of a section's rows (up to
 *  1000 links or sources, 250 countries), so going back and forth cannot grow them without end. */
const KEPT_KEYS = 8;

/** `value`, read under `sig`, as the newest answer after `cur`; a ready one is kept by `sig` (the oldest is dropped
 *  past `KEPT_KEYS`). */
const landOn = <V extends { status: string }>(cur: Kept<V> | undefined, sig: string, value: V): Kept<V> => {
  if (value.status !== 'ready') return { sig, value, readyBySig: cur?.readyBySig ?? NOTHING_KEPT };
  const readyBySig = new Map(cur?.readyBySig);
  readyBySig.delete(sig);
  readyBySig.set(sig, value);
  for (const key of readyBySig.keys()) {
    if (readyBySig.size <= KEPT_KEYS) break;
    readyBySig.delete(key);
  }
  return { sig, value, readyBySig };
};
/** What a read shows under `sig`: the ready answer kept for it, else its newest one (loading or failed) if read under
 *  it. */
const under = <V>(entry: Kept<V> | undefined, sig: string): V | undefined =>
  entry?.readyBySig.get(sig) ?? (entry?.sig === sig ? entry.value : undefined);

/** A filter's own picks left out: what its choices are counted under. */
const withoutLinks = (attr: Attribution): Attribution => withoutFacet(attr, 'link');
const withoutSources = (attr: Attribution): Attribution => withoutFacet(attr, 'source');
/** An affiliate's or campaign's count is read under the other filters as picking it leaves them (a campaign clears the
 *  links, an affiliate the campaigns, and with one choice per filter a pick replaces its own filter's; a pick of
 *  `lifted` takes it out of its own filter's exclusion), so it is credited only where the view the pick opens is. */
const choiceBase = (
  kind: ChoiceKind,
  attr: Attribution,
  conditionsAvailable: boolean,
  lifted?: string,
): Attribution => {
  if (!conditionsAvailable) return withoutLinks({ ...attr, links: null, campaigns: null });
  if (kind === 'campaigns') {
    const left = attr.campaigns?.values.filter((v) => v !== lifted) ?? [];
    const campaigns =
      lifted === undefined ? attr.campaigns : left.length > 0 ? { op: 'out' as const, values: left } : null;
    return withoutLinks({ ...attr, links: null, campaigns });
  }
  const links =
    lifted !== undefined && attr.links
      ? { ...attr.links, exceptAffiliates: attr.links.exceptAffiliates?.filter((id) => id !== lifted) }
      : attr.links;
  return withoutLinks({ ...attr, campaigns: null, links });
};
/** What an affiliate's or campaign's count reads: its own visitors, under the filters picking it leaves. */
const choiceAttribution = (
  kind: ChoiceKind,
  value: string,
  attr: Attribution,
  conditionsAvailable: boolean,
  lifted: string | undefined,
): Attribution => ({
  ...choiceBase(kind, attr, conditionsAvailable, lifted),
  links: kind === 'affiliates' ? { ...emptyLinks(), affiliates: [{ id: value, exceptLinks: [] }] } : null,
  campaigns: kind === 'campaigns' ? { op: 'in', values: [value] } : null,
});
/** Whether the link picker counts credited: a link is picked as an affiliate is (the campaigns cleared, and with one
 *  choice per filter the links replaced), so its links are credited where the filters that pick leaves are. */
export const linksCredited = (attr: Attribution, conditionsAvailable: boolean): boolean =>
  creditedOnly(choiceBase('affiliates', attr, conditionsAvailable));

export type CountSignatures = {
  countries: string;
  sources: string;
  links: string;
  affiliates: string;
  campaigns: string;
};

/** The cards' figures stand in for a picker's while the picker counts under the applied filters (`appliedKey` is
 *  `draftKey`) and the same way: the cards count credited whenever the applied filters are, while a picker counts as
 *  picking its choice leaves them (`credited`: the link picker's `linksCredited`, the source picker's `creditedOnly` of
 *  its filters). An empty card result has nothing to reuse. */
export const canReuseFilterCounts = (
  applied: Attribution,
  appliedKey: string,
  draftKey: string,
  credited: boolean,
): boolean => !selectionEmpty(applied) && appliedKey === draftKey && credited === creditedOnly(applied);

/** Whether the cards' links stand in for the link picker's: read under the same filters (the same key) and counted the
 *  same way (see `linksCredited`). */
export const canReuseLinks = (
  applied: Attribution,
  draft: Attribution,
  appliedKey: string,
  draftKey: string,
  conditionsAvailable: boolean,
): boolean => canReuseFilterCounts(applied, appliedKey, draftKey, linksCredited(draft, conditionsAvailable));

/** The link picker's row statuses: "No link" is always counted credited, so it is unavailable whenever pages are
 *  picked; a link is while the links count credited with pages picked (`linksUnavailable`); else as the links read. */
export const linkRowStatuses = (
  pagesPicked: boolean,
  linksUnavailable: boolean,
  status: Section<unknown>['status'],
): { noLink: ChoiceCount['status']; link: ChoiceCount['status'] } => ({
  noLink: pagesPicked ? 'unavailable' : status,
  link: linksUnavailable ? 'unavailable' : status,
});

/** The affiliates the link picker counts: those of these links (the period's and the brand's) and those the link picks
 *  name, picked or left out, whole or through one of their links. */
export const linkPickerAffiliates = (linkIds: readonly string[], picks: ReadonlyArray<LinkPick | null>): string[] => [
  ...new Set(
    [
      ...linkIds.map(affiliateOf),
      ...picks.flatMap((pick) =>
        pick
          ? [
              ...pick.affiliates.map((v) => v.id),
              ...(pick.exceptAffiliates ?? []),
              ...pick.exact.map(affiliateOf),
              ...(pick.allExcept ?? []).map(affiliateOf),
            ]
          : [],
      ),
    ].filter((id): id is string => id !== null),
  ),
];

/** The pickers that say why a count reads "-" (a credited count cannot be split by page): the sources and the links
 *  whenever pages are picked (Unknown and "No link" are always counted credited), the campaigns while one of theirs is
 *  unavailable. */
export const unavailableSections = (
  pagesPicked: boolean,
  campaigns: ReadonlyMap<string, ChoiceCount>,
): { sources: boolean; links: boolean; campaigns: boolean } => ({
  sources: pagesPicked,
  links: pagesPicked,
  campaigns: [...campaigns.values()].some((c) => c.status === 'unavailable'),
});

/** A filter's values in one order (the order they were ticked in does not change what they count). */
const canonMatch = (m: Match | null | undefined): Match | null =>
  m ? { op: m.op, values: [...m.values].sort() } : null;

/** An attribution with every field in one place and order, so two that count the same sign the same (the filters
 *  build theirs with different fields, in different orders). */
const canonAttribution = (attr: Attribution): Record<string, unknown> => ({
  ...canonicalSelection(attr),
  campaigns: canonMatch(attr.campaigns),
  forceCredited: creditedOnly(attr),
});

type SignatureInput = Pick<
  FilterCountsInput,
  | 'tenantId'
  | 'hosts'
  | 'pages'
  | 'pagesOut'
  | 'span'
  | 'attribution'
  | 'countries'
  | 'attributionAvailable'
  | 'conditionsAvailable'
>;

/** Keys a count by the filters it is read under: these attribution filters and countries, with the brand, its
 *  addresses, the pages and the period. */
const signer = (input: SignatureInput): ((attr: Attribution, countries: Match | null) => string) => {
  const { tenantId, hosts, pages, pagesOut, span, attributionAvailable, conditionsAvailable } = input;
  const base = {
    tenantId,
    hosts: hosts.map((h) => `${h.kind}:${h.host}`),
    pages: pages.map((p) => `${p.kind}:${p.path}`).sort(),
    pagesOut: !!pagesOut,
    span,
    attributionAvailable,
    conditionsAvailable,
  };
  return (attr, countries) =>
    JSON.stringify({ base, attribution: canonAttribution(attr), countries: canonMatch(countries) });
};

/**
 * The filters each filter's counts are read under, as keys (its own picks left out: a country's count leaves the
 * countries out, a source's the sources, a link's, affiliate's or campaign's what picking it clears or replaces). A
 * count is asked again only once its key changes; the page compares the applied filters' keys with the draft's to reuse
 * the cards' own figures. An affiliate or campaign its own filter excludes is keyed on its own (see `useFilterCounts`).
 * Pure.
 */
export const countSignatures = (input: SignatureInput): CountSignatures => {
  const { attribution, countries, conditionsAvailable } = input;
  const sign = signer(input);
  // A link is picked as an affiliate is, so the links and the affiliates are counted under the same filters.
  const picked = sign(choiceBase('affiliates', attribution, conditionsAvailable), countries);
  return {
    countries: sign(attribution, null),
    sources: sign(withoutSources(attribution), countries),
    links: picked,
    affiliates: picked,
    campaigns: sign(choiceBase('campaigns', attribution, conditionsAvailable), countries),
  };
};

/** A read's place is free once it settles, or at once when it is dropped (an aborted read may still be answering). */
const settledOrDropped = ({ signal, read }: Queued): Promise<void> =>
  new Promise((resolve) => {
    const free = (): void => {
      signal.removeEventListener('abort', free);
      resolve();
    };
    signal.addEventListener('abort', free);
    read().then(free, free);
  });

/** Starts the waiting reads while fewer than `CONCURRENCY` hold a place; each freed place starts the next. */
const drain = (queue: Queue): void => {
  while (queue.running < CONCURRENCY) {
    const next = queue.pending.shift();
    if (!next) return;
    if (next.signal.aborted) continue;
    queue.running += 1;
    void settledOrDropped(next).then(() => {
      queue.running -= 1;
      drain(queue);
    });
  }
};

/**
 * The Website sheet's filter counts, read on demand (for the filter on screen) rather than on every load: each choice's
 * visitors, asked exactly as the cards would ask once it is picked, under the other filters as they stand in the sheet,
 * so clearing or changing one there re-counts the rest (a filter's choices are counted without its own picks, so ticking
 * them reads nothing again). Each filter's counts are kept under the filters they were read with (see `Kept`) and asked
 * again once those change; only the newest answer of each lands. Back under filters a count was read with, an affiliate
 * or campaign count shows that one and is not read again; a section (the countries, sources, links) shows its kept rows
 * and is read again, since it lists the choices themselves and a fresh read can bring ones they lack.
 */
export const useFilterCounts = (input: FilterCountsInput): FilterCounts => {
  const {
    tenantId,
    ready,
    hosts,
    pages,
    pagesOut,
    span,
    attribution,
    countries,
    attributionAvailable,
    conditionsAvailable,
    listedLinks,
    want,
  } = input;
  const [held, setHeld] = React.useState<Held>({});
  // The filters each count was last asked under (a failed read forgets its own, so it is asked again when shown).
  const asked = React.useRef(new Map<string, string>());
  const controllers = React.useRef(new Map<string, AbortController>());
  // One queue for every read the counts ask, however many syncs add to it.
  const queue = React.useRef<Queue>({ pending: [], running: 0 });

  const sigs = countSignatures(input);
  const sign = signer(input);
  // The links this hook's links read found, while they stand under the filters now.
  const linkRows = want.links ? under(held.links, sigs.links)?.data ?? [] : [];
  // The value a pick takes out of its own filter's exclusion, if it does (as `pickInSheet` does): a left-out campaign,
  // or a left-out affiliate not ticked back through every link the picker lists for it.
  const listed = [...listedLinks, ...linkRows.map((l) => l.affId)];
  const liftOf = (kind: ChoiceKind, value: string): string | undefined => {
    const lifts =
      conditionsAvailable &&
      (kind === 'campaigns'
        ? attribution.campaigns?.op === 'out' && attribution.campaigns.values.includes(value)
        : liftsExclusion(
            attribution.links,
            value,
            listed.filter((l) => affiliateOf(l) === value),
          ));
    return lifts ? value : undefined;
  };
  // A choice's count is keyed by its filter's key, unless picking it takes it out of that filter's exclusion (its own
  // filters differ from the other choices' then).
  const sigOf = (kind: ChoiceKind, value: string): string => {
    const lifted = liftOf(kind, value);
    return lifted === undefined
      ? sigs[kind]
      : sign(choiceBase(kind, attribution, conditionsAvailable, lifted), countries);
  };
  // The link picker's affiliates are counted beside the ones asked for, once its links land (under the filters then).
  // Without attribution no affiliate or campaign is counted.
  const affiliateChoices = attributionAvailable
    ? [
        ...new Set([
          ...want.affiliates,
          ...linkRows.map((l) => affiliateOf(l.affId)).filter((id): id is string => id !== null),
        ]),
      ]
    : NO_VALUES;
  const choices: Record<ChoiceKind, readonly string[]> = {
    affiliates: affiliateChoices,
    campaigns: attributionAvailable ? want.campaigns : NO_VALUES,
  };
  // A credited count cannot be split by page: with pages picked such a choice is unavailable, which needs no read.
  const unavailable = (kind: ChoiceKind, value: string): boolean =>
    pages.length > 0 &&
    creditedOnly(choiceAttribution(kind, value, attribution, conditionsAvailable, liftOf(kind, value)));
  // What is asked, and which affiliates a pick takes back (that changes with the links the picker lists).
  const wantKey = JSON.stringify({
    ...want,
    affiliates: affiliateChoices,
    lifted: affiliateChoices.filter((id) => liftOf('affiliates', id) !== undefined),
  });

  // Reads still running are dropped when the counts are no longer shown, and forgotten, so a remount (StrictMode, Fast
  // Refresh) asks them again; what was read stays read.
  React.useEffect(() => {
    const running = controllers.current;
    const askedFor = asked.current;
    return () => {
      running.forEach((c, key) => {
        c.abort();
        askedFor.delete(key);
      });
      running.clear();
    };
  }, []);

  // Syncs with the analytics service: counts the filter on screen, unless already asked under these filters.
  React.useEffect(() => {
    const toRead = (kind: ChoiceKind): string[] => choices[kind].filter((value) => !unavailable(kind, value));
    // Reads for counts no longer on screen (another filter shown, the sheet closed) are dropped and asked again when
    // shown, so they never queue beside the cards' reads.
    const wanted = new Set([
      ...(want.countries ? ['countries'] : []),
      ...(want.sources && attributionAvailable ? ['sources'] : []),
      ...(want.links && attributionAvailable ? ['links'] : []),
      ...CHOICE_KINDS.flatMap((kind) => toRead(kind).map((value) => `${kind}:${value}`)),
    ]);
    controllers.current.forEach((c, key) => {
      if (wanted.has(key)) return;
      c.abort();
      controllers.current.delete(key);
      asked.current.delete(key);
      // A dropped choice's placeholder goes with it (back to the last count it read ready, if any), so none stays on a
      // choice still listed.
      CHOICE_KINDS.forEach((kind) => {
        if (!key.startsWith(`${kind}:`)) return;
        const value = key.slice(kind.length + 1);
        setHeld((h) => {
          const entry = h[kind]?.get(value);
          if (entry?.value.status !== 'loading') return h;
          const counts = new Map(h[kind]);
          const last = [...entry.readyBySig].pop();
          if (last) counts.set(value, { sig: last[0], value: last[1], readyBySig: entry.readyBySig });
          else counts.delete(value);
          return { ...h, [kind]: counts };
        });
      });
    });
    if (tenantId === undefined || !ready) return;
    // A section's read (the countries, the sources, the links) goes ahead of the counts already waiting, in the order
    // asked.
    let ahead = 0;
    const enqueue = (signal: AbortSignal, read: () => Promise<void>, first = false): void => {
      const { pending } = queue.current;
      pending.splice(first ? ahead : pending.length, 0, { signal, read });
      if (first) ahead += 1;
    };
    // A count to ask under `sig`: its earlier read (under other filters) is dropped. Null: asked already.
    const start = (key: string, sig: string): AbortSignal | null => {
      if (asked.current.get(key) === sig) return null;
      asked.current.set(key, sig);
      controllers.current.get(key)?.abort();
      const controller = new AbortController();
      controllers.current.set(key, controller);
      return controller.signal;
    };
    // A read over (answered or failed): no longer running. A failed one is asked again when shown.
    const done = (key: string, signal: AbortSignal): void => {
      if (controllers.current.get(key)?.signal === signal) controllers.current.delete(key);
    };
    // A failed read is asked again when shown; an aborted one was already forgotten (and a newer read of the same count
    // may stand in its place since).
    const forget = (key: string, sig: string, signal: AbortSignal): void => {
      if (!signal.aborted && asked.current.get(key) === sig) asked.current.delete(key);
    };
    const askerFor = (signal: AbortSignal, attr: Attribution, inCountries: Match | null) => {
      const scope = scopeOf(pages, hosts, !!pagesOut);
      return makeAsker({
        tenantId,
        signal,
        attr: () => attr,
        countries: inCountries,
        scope: () => scope,
        attributing: () => attributionAvailable && attributionActive(attr),
        conditionsAvailable: () => conditionsAvailable,
      });
    };

    // A section is read again under filters it was read with before (its kept rows show meanwhile): it lists the
    // choices themselves, and a fresh read can bring ones they lack.
    if (want.countries) {
      const sig = sigs.countries;
      const signal = start('countries', sig);
      if (signal) {
        setHeld((h) => ({ ...h, countries: landOn(h.countries, sig, LOADING) }));
        enqueue(
          signal,
          async () => {
            try {
              const attr = await expandLinkExclusions(askerFor(signal, attribution, null).askOne, [span], attribution);
              const rows = shapeCountries(await askerFor(signal, attr, null).ask(countriesQuery(span)));
              if (!signal.aborted)
                setHeld((h) => ({ ...h, countries: landOn(h.countries, sig, { status: 'ready', data: rows }) }));
            } catch {
              forget('countries', sig, signal);
              if (!signal.aborted) setHeld((h) => ({ ...h, countries: landOn(h.countries, sig, FAILED) }));
            } finally {
              done('countries', signal);
            }
          },
          true,
        );
      }
    }

    if (want.sources && attributionAvailable) {
      const sig = sigs.sources;
      const signal = start('sources', sig);
      if (signal) {
        setHeld((h) => ({
          ...h,
          sources: landOn(h.sources, sig, LOADING),
          sourceReach: landOn(h.sourceReach, sig, LOADING),
        }));
        enqueue(
          signal,
          async () => {
            try {
              const selected = withoutSources(attribution);
              const attr = await expandLinkExclusions(askerFor(signal, selected, countries).askOne, [span], selected);
              const { askOne } = askerFor(signal, attr, countries);
              const rows = shapeSources(await askOne(sourcesQuery(span, attr)));
              const reach = await readSourceReach(askOne, span, attr);
              if (signal.aborted) return;
              setHeld((h) => ({
                ...h,
                sources: landOn(h.sources, sig, { status: 'ready', data: rows }),
                sourceReach: landOn(h.sourceReach, sig, { status: 'ready', data: reach }),
              }));
            } catch {
              forget('sources', sig, signal);
              if (signal.aborted) return;
              setHeld((h) => ({
                ...h,
                sources: landOn(h.sources, sig, FAILED),
                sourceReach: landOn(h.sourceReach, sig, FAILED),
              }));
            } finally {
              done('sources', signal);
            }
          },
          true,
        );
      }
    }

    if (want.links && attributionAvailable) {
      const sig = sigs.links;
      const signal = start('links', sig);
      if (signal) {
        setHeld((h) => ({ ...h, links: landOn(h.links, sig, LOADING) }));
        enqueue(
          signal,
          async () => {
            try {
              const attr = choiceBase('affiliates', attribution, conditionsAvailable);
              const { askOne } = askerFor(signal, attr, countries);
              const rows = await readLinks(askOne, span, attr);
              if (signal.aborted) return;
              setHeld((h) => ({ ...h, links: landOn(h.links, sig, { status: 'ready', data: rows }) }));
            } catch {
              forget('links', sig, signal);
              if (!signal.aborted) setHeld((h) => ({ ...h, links: landOn(h.links, sig, FAILED) }));
            } finally {
              done('links', signal);
            }
          },
          true,
        );
      }
    }

    const choice = (kind: ChoiceKind, value: string): void => {
      const key = `${kind}:${value}`;
      const sig = sigOf(kind, value);
      const signal = start(key, sig);
      if (!signal) return;
      // A count lands beside those read ready under other filters (see `landOn`).
      const land = (count: ChoiceCount): void => {
        if (signal.aborted) return;
        setHeld((h) => ({ ...h, [kind]: new Map(h[kind]).set(value, landOn(h[kind]?.get(value), sig, count)) }));
      };
      // A count read ready under these filters before (others were tried since) shows again, is not asked again, and
      // lands again as the newest one kept (`held` as this render saw it, see the deps below).
      const before = under(held[kind]?.get(value), sig);
      if (before?.status === 'ready') {
        land(before);
        done(key, signal);
        return;
      }
      const attr = choiceAttribution(kind, value, attribution, conditionsAvailable, liftOf(kind, value));
      land({ status: 'loading', visitors: 0 });
      enqueue(signal, async () => {
        try {
          const rows = await askerFor(signal, attr, countries).ask(visitorsQuery(span));
          land({ status: 'ready', visitors: num(rows[0]?.visitors) });
        } catch {
          forget(key, sig, signal);
          land({ status: 'error', visitors: 0 });
        } finally {
          done(key, signal);
        }
      });
    };
    CHOICE_KINDS.forEach((kind) => toRead(kind).forEach((value) => choice(kind, value)));

    drain(queue.current);
    // The inputs are read through the signatures and `wantKey`: `sigs.countries` carries every filter but the
    // countries, the other signatures carry the countries, and `wantKey` what is asked and which affiliates a pick takes
    // back, so a choice's own key (`sigOf`) never changes without one of them. `held` is read directly only for a
    // count read ready before (an outdated one can only miss such a count, which is then read again, never invent
    // one); the links it holds reach here through `linkRows`, which `wantKey` carries (the affiliates they bring, and
    // which ones a pick takes back).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sigs.countries, sigs.sources, sigs.links, sigs.affiliates, sigs.campaigns, wantKey, ready]);

  // Each section as it stands under the filters now: read under them, or kept from an earlier read under them (one read
  // under other filters only counts as not read yet).
  const current = <T>(entry: Entry<T> | undefined, sig: string): Section<T> => under(entry, sig) ?? LOADING;
  const shown = (kind: ChoiceKind): ReadonlyMap<string, ChoiceCount> => {
    const counts = new Map<string, ChoiceCount>();
    held[kind]?.forEach((entry, value) => {
      const count = under(entry, sigOf(kind, value));
      if (count) counts.set(value, count);
    });
    choices[kind].forEach((value) => {
      if (unavailable(kind, value)) counts.set(value, UNAVAILABLE);
    });
    return counts;
  };
  // The same maps while nothing they show changes, so the pickers' options are not built again on every render.
  // `shown` reads the held counts, the filters (the signatures, as the sync above) and what is asked (`wantKey`).
  /* eslint-disable react-hooks/exhaustive-deps */
  const affiliates = React.useMemo(
    () => shown('affiliates'),
    [held.affiliates, sigs.countries, sigs.affiliates, wantKey],
  );
  const campaigns = React.useMemo(() => shown('campaigns'), [held.campaigns, sigs.countries, sigs.campaigns, wantKey]);
  /* eslint-enable react-hooks/exhaustive-deps */
  const countriesHeld = under(held.countries, sigs.countries);
  return {
    countries: countriesHeld
      ? { status: countriesHeld.status, rows: countriesHeld.data ?? [] }
      : { status: want.countries ? 'loading' : 'idle', rows: [] },
    affiliates,
    campaigns,
    sources: current(held.sources, sigs.sources),
    sourceReach: current(held.sourceReach, sigs.sources),
    links: current(held.links, sigs.links),
    linksUnavailable: pages.length > 0 && linksCredited(attribution, conditionsAvailable),
  };
};
